import React, {useState, useEffect} from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
    Box,
    Button,
    CircularProgress,
    Typography,
    TextField,
    Paper,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Container,
    Alert
} from '@mui/material';
import {useSelector} from "react-redux";
import {useNavigate, useLocation} from "react-router-dom";
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import SearchIcon from '@mui/icons-material/Search';
import BuildIcon from '@mui/icons-material/Build';
import MobileRestriction from '../../../components/MobileRestriction';
import {BACKEND_API_URL} from "../../../utils/api";

const REACT_APP_BACKEND_API_URL = BACKEND_API_URL;

const DeepFind = () => {
    const user = useSelector((state) => state.auth.user);
    const navigate = useNavigate();
    const location = useLocation();
    
    // State
    const [isLoading, setIsLoading] = useState(false);
    const [folderId, setFolderId] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [results, setResults] = useState([]);
    const [allResults, setAllResults] = useState([]);
    const [itemInfo, setItemInfo] = useState(null);
    const [error, setError] = useState(null);
    const [clientFilter, setClientFilter] = useState('');
    const [isFixingPermissions, setIsFixingPermissions] = useState(false);

    // Parse query params on mount
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const folderParam = params.get('folder');
        const queryParam = params.get('query');
        
        if (folderParam) {
            setFolderId(folderParam);
            if (queryParam) {
                setSearchQuery(queryParam);
            }
            // Auto-search if folder param is present
            performSearch(folderParam, queryParam || '');
        }
    }, [location.search]);

    const performSearch = async (folder, query) => {
        if (!folder) {
            setError('Please enter a folder ID');
            return;
        }

        setIsLoading(true);
        setError(null);
        
        try {
            const token = Cookies.get('access_token');
            const params = new URLSearchParams({
                folder: folder,
            });
            
            if (query) {
                params.append('query', query);
            }

            const response = await axios.get(
                `${REACT_APP_BACKEND_API_URL}/api/deep-search/?${params.toString()}`,
                {
                    headers: {
                        Authorization: `Token ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            const files = response.data.files || [];
            setResults(files);
            setAllResults(files);
            setItemInfo({
                id: response.data.item_id,
                name: response.data.item_name,
                gdrive_folder_id: response.data.gdrive_folder_id,
            });
            
            if (files.length === 0) {
                if (query) {
                    toast.error('No files found. Google Drive content search is unreliable. Try searching by filename or leave query blank to see all files.');
                } else {
                    toast.success('Search completed - no files in this folder');
                }
            } else {
                toast.success(`Found ${files.length} file(s)`);
            }
        } catch (err) {
            console.error('Search error:', err);
            console.error('Error response:', err.response?.data);
            const errorMsg = err.response?.data?.error || 'Failed to search Google Drive';
            const details = err.response?.data?.details;
            const fullError = details && details !== errorMsg ? `${errorMsg}\n\nDetails: ${details}` : errorMsg;
            setError(fullError);
            toast.error(errorMsg);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = () => {
        // Update URL with search params
        const params = new URLSearchParams();
        params.set('folder', folderId);
        if (searchQuery) {
            params.set('query', searchQuery);
        }
        navigate(`/deep-find?${params.toString()}`);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const handleClientFilter = (filterText) => {
        setClientFilter(filterText);
        if (!filterText) {
            setResults(allResults);
            return;
        }
        
        const filtered = allResults.filter(file => 
            file.name.toLowerCase().includes(filterText.toLowerCase())
        );
        setResults(filtered);
    };

    const handleFixPermissions = async () => {
        if (!itemInfo) return;
        
        setIsFixingPermissions(true);
        try {
            const token = Cookies.get('access_token');
            const response = await axios.post(
                `${REACT_APP_BACKEND_API_URL}/api/folder/${itemInfo.id}/fix_drive_permissions/`,
                {},
                {
                    headers: {
                        Authorization: `Token ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            
            toast.success('Permissions fixed! Try searching again in a few minutes.');
        } catch (err) {
            console.error('Fix permissions error:', err);
            const errorMsg = err.response?.data?.error || 'Failed to fix permissions';
            toast.error(errorMsg);
        } finally {
            setIsFixingPermissions(false);
        }
    };

    const getFileIcon = (file) => {
        if (file.isFolder) {
            return <FolderIcon sx={{color: '#1976d2'}} />;
        }
        
        const mimeType = file.mimeType || '';
        
        if (mimeType.includes('document')) {
            return <DescriptionIcon sx={{color: '#4285f4'}} />;
        } else if (mimeType.includes('spreadsheet')) {
            return <TableChartIcon sx={{color: '#0f9d58'}} />;
        } else if (mimeType.includes('presentation')) {
            return <InsertDriveFileIcon sx={{color: '#f4b400'}} />;
        }
        
        return <InsertDriveFileIcon />;
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '-';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
        return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    };

    // Mobile portrait mode check
    const mobilePortrait = window?.innerWidth <= 600;
    if (mobilePortrait) {
        return <MobileRestriction />;
    }

    return (
        <Box sx={{minHeight: '100vh', bgcolor: '#f5f5f5'}}>
            <Container maxWidth="lg" sx={{py: 4}}>
                <Paper sx={{p: 3, mb: 3}}>
                    <Typography variant="h4" gutterBottom sx={{mb: 3}}>
                        Deep Find - Google Drive Search
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                        Search for files within a specific folder's Google Drive. Enter the item ID and optionally a search query.
                    </Typography>
                    <Alert severity="warning" sx={{mb: 3, fontSize: '0.875rem'}}>
                        <strong>⚠️ Google Drive Content Search Limitation:</strong>
                        <ul style={{margin: '4px 0', paddingLeft: '20px'}}>
                            <li><strong>Recommended:</strong> Leave query blank to fetch all files, then use client-side filter below</li>
                            <li>Google Drive's content search is unreliable and may take hours/days to index new content</li>
                            <li>The "Search Query" field searches both filenames and content via Google Drive API</li>
                            <li>The "Filter by filename" field (shown after search) does instant client-side filtering</li>
                        </ul>
                    </Alert>

                    <Box sx={{display: 'flex', gap: 2, mb: 2}}>
                        <TextField
                            label="Folder/Item ID"
                            value={folderId}
                            onChange={(e) => setFolderId(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Enter item ID"
                            size="small"
                            sx={{flex: 1}}
                            disabled={isLoading}
                        />
                        <TextField
                            label="Search Query (optional)"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder="Enter search text"
                            size="small"
                            sx={{flex: 2}}
                            disabled={isLoading}
                        />
                        <Button
                            variant="contained"
                            onClick={handleSearch}
                            disabled={isLoading || !folderId}
                            startIcon={isLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                            sx={{minWidth: 120}}
                        >
                            {isLoading ? 'Searching...' : 'Search'}
                        </Button>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{mb: 2, whiteSpace: 'pre-wrap'}}>
                            {error}
                        </Alert>
                    )}
                </Paper>

                {itemInfo && (
                    <Paper sx={{p: 2, mb: 3, bgcolor: '#e3f2fd'}}>
                        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1}}>
                            <Box>
                                <Typography variant="subtitle1" sx={{fontWeight: 'bold'}}>
                                    Searching in: {itemInfo.name} (ID: {itemInfo.id})
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Google Drive Folder ID: {itemInfo.gdrive_folder_id}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Results: {results.length} of {allResults.length} file(s)
                                </Typography>
                            </Box>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={isFixingPermissions ? <CircularProgress size={16} /> : <BuildIcon />}
                                onClick={handleFixPermissions}
                                disabled={isFixingPermissions}
                                sx={{minWidth: 150}}
                            >
                                {isFixingPermissions ? 'Verifying...' : 'Verify Permissions'}
                            </Button>
                        </Box>
                        
                        <Alert severity="info" sx={{mt: 2, fontSize: '0.8rem'}}>
                            Note: Folders inherit Morphle group permissions from the root folder (company-only access). 
                            The "Fix Permissions" button verifies this configuration if search isn't working properly.
                        </Alert>
                        
                        {allResults.length > 0 && (
                            <Box sx={{mt: 2}}>
                                <TextField
                                    label="Filter by filename (client-side)"
                                    value={clientFilter}
                                    onChange={(e) => handleClientFilter(e.target.value)}
                                    placeholder="Type to filter results..."
                                    size="small"
                                    fullWidth
                                    helperText="This filters the results shown below by filename only"
                                />
                            </Box>
                        )}
                    </Paper>
                )}

                {results.length > 0 && (
                    <Paper sx={{p: 2}}>
                        <Typography variant="h6" gutterBottom>
                            Search Results ({results.length})
                        </Typography>
                        <List>
                            {results.map((file, index) => (
                                <ListItem
                                    key={file.id || index}
                                    disablePadding
                                    sx={{
                                        borderBottom: index < results.length - 1 ? '1px solid #e0e0e0' : 'none',
                                    }}
                                >
                                    <ListItemButton
                                        component="a"
                                        href={file.webViewLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <ListItemIcon>
                                            {getFileIcon(file)}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={file.name}
                                            secondary={
                                                <Box component="span">
                                                    <Typography component="span" variant="body2" color="text.secondary">
                                                        {file.isFolder ? 'Folder' : 'File'}
                                                        {' • '}
                                                        {formatFileSize(file.size)}
                                                        {' • '}
                                                        Modified: {formatDate(file.modifiedTime)}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                )}

                {!isLoading && results.length === 0 && itemInfo && (
                    <Paper sx={{p: 4, textAlign: 'center'}}>
                        <Typography variant="body1" color="text.secondary">
                            No files found in this folder.
                        </Typography>
                    </Paper>
                )}
            </Container>
        </Box>
    );
};

export default DeepFind;

